import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { IoCloseOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import {
  createReferral,
  updateReferral,
} from "../../../../../../../services/referral.services";

// border-[1px] rather than `border`: Bootstrap's !important .border would
// otherwise force its grey and hide the focus and error colours. The rings
// keep focus and errors visible in dark mode, where the theme shim sets every
// input's border colour. Padding is in px because Bootstrap's !important
// .px-3 would cancel the right padding that makes room for the "%".
const inputCls =
  "w-full h-10 pl-[16px] pr-[32px] border-[1px] rounded-lg text-sm text-ui-text bg-ui-surface outline-none focus:ring-1 focus:ring-brand transition-all";
const inputStateCls = (hasError) =>
  hasError
    ? "border-state-danger ring-1 ring-state-danger"
    : "border-ui-border focus:border-brand";

// Max users is no longer asked from the user; it is always sent as this default.
const DEFAULT_MAX_USERS = 100000;

// Flat-amount ($) referrals aren't tested yet, so every referral is a
// percentage for now.
const MARKUP_TYPE = "%";

const emptyData = { markup: "" };

export default function ReferralFormModal({
  open,
  setOpen,
  previousData,
  onSaved,
  setSnack,
}) {
  const { t } = useTranslation("settings");
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!previousData?.id;

  useEffect(() => {
    if (open) {
      setData(previousData ? { markup: previousData.markup ?? "" } : emptyData);
      setErrors({});
    }
  }, [open, previousData]);

  const handleClose = () => setOpen(false);

  const validate = () => {
    const e = {};
    if (data.markup === "" || isNaN(Number(data.markup)))
      e.markup = t("referrals.form.rateRequired");
    else if (Number(data.markup) < 0)
      e.markup = t("referrals.form.rateNegative");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        markup: Number(data.markup),
        markup_type: MARKUP_TYPE,
        max_users: previousData?.max_users ?? DEFAULT_MAX_USERS,
      };
      const res = isEdit
        ? await updateReferral(previousData.id, payload)
        : await createReferral(payload);
      if (res?.error) {
        setSnack?.((s) => ({
          ...s,
          open: true,
          text:
            res?.error?.response?.data?.message ?? t("referrals.form.saveFailed"),
          severity: "error",
        }));
        return;
      }
      setSnack?.((s) => ({
        ...s,
        open: true,
        text: res?.data?.message ?? t("referrals.form.saved"),
        severity: "success",
      }));
      onSaved?.(res?.data?.result);
      handleClose();
    } catch (error) {
      setSnack?.((s) => ({
        ...s,
        open: true,
        text: error?.response?.data?.message ?? t("referrals.form.saveFailed"),
        severity: "error",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="referral-form-title"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-ui-surface rounded-xl shadow-2xl w-[480px] max-w-[95vw] max-h-[90vh] flex flex-col outline-none"
      >
        <div className="flex items-center justify-between px-6 py-3 border-b border-ui-border">
          <h2
            id="referral-form-title"
            className="text-lg font-semibold text-ui-text m-0 leading-none"
          >
            {isEdit ? t("referrals.editReferral") : t("referrals.createReferral")}
          </h2>
          <button
            type="button"
            aria-label={t("common:actions.close")}
            onClick={handleClose}
            className="text-ui-text-subtle hover:text-ui-text transition-colors"
          >
            <IoCloseOutline size={22} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-[20px] flex-1 flex flex-col gap-4">
          <div>
            <label
              htmlFor="referral-markup"
              className="block text-xs font-medium text-ui-text-muted mb-1"
            >
              {t("referrals.form.rateLabel")}
            </label>
            <div className="relative">
              <input
                id="referral-markup"
                className={`${inputCls} ${inputStateCls(!!errors.markup)}`}
                placeholder={t("referrals.form.ratePlaceholder")}
                type="number"
                min="0"
                value={data.markup}
                onChange={(e) =>
                  setData((d) => ({ ...d, markup: e.target.value }))
                }
              />
              <span className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 text-sm text-ui-text-subtle">
                %
              </span>
            </div>
            {errors.markup && (
              <p className="mb-0 mt-1 text-state-danger-text text-xs">
                {errors.markup}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t border-ui-border">
          <BoltButton
            label={t("common:actions.cancel")}
            variant="outline"
            size="md"
            onClick={handleClose}
          />
          <BoltButton
            label={isEdit ? t("actions.update") : t("actions.create")}
            variant="primary"
            size="md"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Modal>
  );
}
